import { useState, useEffect, useRef, useCallback } from "react";
import axios, { CancelTokenSource } from "axios";

export interface Idle {
  type: "idle";
}

export interface Pending {
  type: "pending";
}

export interface Done<T> {
  type: "done";
  data: T;
}

export interface Fail {
  type: "fail";
  error: unknown;
}

export type State<T> = Idle | Pending | Done<T> | Fail;

export type PromiseFn<T> = (cancelToken: CancelTokenSource) => Promise<T>;

export const useFetch = <T>() => {
  const cancelToken = useRef<CancelTokenSource | null>(null);
  const [state, setState] = useState<State<T>>({ type: "idle" });

  const abort = () => {
    cancelToken?.current?.cancel();
  };

  const handleFetch = useCallback(async (promiseFn: PromiseFn<T>) => {
    abort();
    cancelToken.current = axios.CancelToken.source();

    setState({ type: "pending" });

    try {
      const data = await promiseFn(cancelToken.current);
      setState({ type: "done", data });
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        console.warn("Request canceled");
      }

      setState({ type: "fail", error });
    }
  }, []);

  useEffect(() => {
    return () => {
      abort();
    };
  }, []);

  return [state, handleFetch, abort] as const;
};
